const _ = require("lodash");
const CanvasMath = require("../CanvasMath");
const Math2D = require("../../util/Math2D");
const DrawingUtil = require("../../util/DrawingUtil");
const ArrowDraw = require("../../util/ArrowDraw");
const ImageCache = require("../../util/ImageCache");

//Used to override console.log so anything printed in diagrams will only get printed on Baeditor.
const log = function(...args) {
	if (global.IS_ON_BAEDITOR || process.env.NODE_ENV !== "production") {
		console.log.apply(null, args); // eslint-disable-line no-console
	}
};

const fakeConsole = {log};

//An internal diagram helpers function to make sure that all Canvas Math
//measuring/renderering calls get props that should be passed through
const getCanvasMathProps = function(ctx, props) {
	return _.extend(props, ctx.__cmProps);
};

//This function measures batex using a canvas context.
//It has an optional argument fontProps that is an object that can include a size or family and can have bold or italic set to true.
//Ex: {size: 20, family: "Roboto", bold: true, italic: false}
//If fontProps is not used, the batex will be measured with the current font size/family/style of the context.
//It returns an object with width and height;
const measureBatex = function(ctx, batex, fontProps) {
	if (!ctx) {
		throw new Error("measureBatex: ctx is missing");
	}
	if (!batex) {
		throw new Error("measureBatex: Batex must be set.");
	}
	const props = getCanvasMathProps(ctx, {
		batex: batex.toString(),
		fontInfo: fontProps,
	});
	return CanvasMath.measureBatex(ctx, props).measure;
};

//This function draws batex with the top left corner as the given x, y location.
const drawBatex = function(ctx, batex, x, y) {
	if (!ctx) {
		throw new Error("drawBatex: ctx is missing");
	}
	if (!batex) {
		throw new Error("drawBatex: Batex must be set.");
	}

	const props = getCanvasMathProps(ctx, {batex: batex.toString(), x: x, y: y});
	CanvasMath.render(ctx, props);
};

//This function draws batex with the center as the given x, y location.
const drawBatexAtCenter = function(ctx, batex, x, y, rotation) {
	if (!ctx) {
		throw new Error("drawBatexAtCenter: ctx is missing");
	}
	if (!batex) {
		throw new Error("drawBatexAtCenter: Batex must be set.");
	}

	let measure, props;
	if (rotation) {
		ctx.save();
		ctx.translate(x, y);
		ctx.rotate((rotation * Math.PI) / 180);
		measure = CanvasMath.measureBatex(
			ctx,
			getCanvasMathProps(ctx, {batex: batex.toString()})
		).measure;
		props = getCanvasMathProps(ctx, {
			batex: batex.toString(),
			x: (-1 * measure.width) / 2,
			y: (-1 * measure.height) / 2,
		});
		CanvasMath.render(ctx, props);
		ctx.restore();
	} else {
		measure = CanvasMath.measureBatex(
			ctx,
			getCanvasMathProps(ctx, {batex: batex.toString()})
		).measure;
		props = getCanvasMathProps(ctx, {
			batex: batex.toString(),
			x: x - measure.width / 2,
			y: y - measure.height / 2,
		});
		CanvasMath.render(ctx, props);
	}
};

//This function fits batex into a given rectangle on the canvas. The rectangle
//is specified by its top left x and y, width, and height. It does not
//draw/outline the rectangle.
const drawBatexInRect = function(ctx, batex, x, y, w, h, extraProps) {
	if (!ctx) {
		throw new Error("drawBatexInRect: ctx is missing");
	}
	if (!batex) {
		throw new Error("drawBatexInRect: Batex must be set.");
	}

	let props = _.extend(extraProps, {
		batex: batex.toString(),
		rect: [x, y, w, h],
	});
	props = getCanvasMathProps(ctx, props);
	CanvasMath.drawInRect(ctx, props);
};

const getArrowDimensions = function(ctx) {
	const arrowLineWidth = ctx.measureText("|").width;
	const headLength = arrowLineWidth * 2.5;
	return {arrowLineWidth, headLength};
};

//This function draws an arrow. It requires beginning and end points for the
//line that the arrow is part of.
const drawArrow = function(ctx, x1, y1, x2, y2) {
	const {arrowLineWidth, headLength} = getArrowDimensions(ctx);

	ArrowDraw.drawArrow(ctx, {
		x1,
		y1,
		x2,
		y2,
		headHeight: (headLength * 2) / 3,
		headLength: headLength,
		lineWidth: arrowLineWidth,
		headCurved: false,
	});
};

const drawQuadArrow = function(ctx, sx, sy, cx, cy, ex, ey, opts = {}) {
	const {arrowSide = "end"} = opts; //Can be set to "end", "start", or "both"
	const {arrowLineWidth, headLength} = getArrowDimensions(ctx);

	//Draw the curve
	ctx.save();
	ctx.beginPath();
	ctx.lineWidth = arrowLineWidth / 3;
	ctx.moveTo(sx, sy);
	ctx.quadraticCurveTo(cx, cy, ex, ey);
	ctx.stroke();
	ctx.restore();

	const drawHead = (x1, y1, x2, y2) => {
		const arrowPositionInfo = {
			startX: x1,
			startY: y1,
			endX: x2,
			endY: y2,
			headLength: headLength,
			headHeight: (headLength * 2) / 3,
			curved: true,
		};
		ArrowDraw.drawArrowHead(
			ctx,
			ArrowDraw.calculateArrowHeadPositions(arrowPositionInfo)
		);
	};

	//Find point along quadratic bezier curve
	const bez = (p1, p2, p3, t) =>
		Math.pow(1 - t, 2) * p1 + 2 * (1 - t) * t * p2 + t * t * p3;

	//Then the arrow heads
	if (arrowSide === "end" || arrowSide === "both") {
		const hx = bez(sx, cx, ex, 0.95);
		const hy = bez(sy, cy, ey, 0.95);
		drawHead(hx, hy, ex, ey);
	}
	if (arrowSide === "start" || arrowSide === "both") {
		const hx = bez(sx, cx, ex, 0.05);
		const hy = bez(sy, cy, ey, 0.05);
		drawHead(hx, hy, sx, sy);
	}
};

const drawToothpick = function(ctx, x1, y1, x2, y2) {
	DrawingUtil.drawToothpick(ctx, {endpoints: [x1, y1, x2, y2]});
};

// Draw a protractor with vertex at x, y, rotated by angle.
// Width is width of entire protractor.
const drawProtractor = function(ctx, x, y, angle, width) {
	const imgSrc =
		global.BASE_URL + "assets/images/elements/measurement/protractor.svg";
	const protractor = ImageCache.src(imgSrc, true);
	ctx.save();
	ctx.rotateAboutDeg(angle, x, y);
	ctx.drawImage(protractor, x - width / 2, y - width / 2, width, width);
	ctx.restore();
};

// Draw a ruler with vertex at x, y, rotated by angle.
//Width is the width of the entire ruler
const drawRuler = function(ctx, x, y, angle, width, type = false) {
	let imgSrc = global.BASE_URL + "assets/images/elements/measurement/";
	// Each ratio is width / height of the asset's viewbox
	let ratio = 56 / 452;
	if (type === "ruler10" || type === "ruler10-video") {
		imgSrc += `${type}.svg`;
		ratio = 56 / 398;
	} else if (type === "unitRuler10") {
		imgSrc += `${type}.svg`;
		ratio = 56 / 363;
	} else if (type === "unitRuler15") {
		imgSrc += `${type}.svg`;
		ratio = 56 / 544;
	} else if (_.isString(type) && type.startsWith("shape")) {
		imgSrc += `${type}.svg`;
		// Handle rulers for videos, like "shape1-video".
		const shapeType = type.split("-").length === 2 ? type.split("-")[0] : type;
		const shapeRatios = {
			shape1: 227 / 359,
			shape2: 245 / 321,
			shape3: 268 / 479,
		};
		ratio = shapeRatios[shapeType];
	} else if (_.isString(type) && type.split("-").length === 2) {
		const range = type.split("-");
		imgSrc += "broken-ruler-" + range[0] + "-" + range[1] + ".svg";
		ratio = 56 / 452;
	} else if (_.isString(type) && type.startsWith("string")) {
		const length = Number(type.slice("string".length)) || 10;
		// try to get right thickness of string
		const adj = (length / 10) * 592;
		ratio = 40 / adj;
		imgSrc += "string.svg";
	} else if (type === "square7") {
		imgSrc += `${type}.svg`;
		ratio = 56 / (56 * 7);
	} else if (type === "square10") {
		imgSrc += `${type}.svg`;
		ratio = 56 / (56 * 10);
	} else if (type === true || type === "fractional") {
		// The true check is for backward compatibility when we only had fractional and ruler12 options.
		imgSrc += "ruler-fractional.svg";
		ratio = 56 / 447;
	} else {
		imgSrc += "ruler12.svg";
	}
	const ruler = ImageCache.src(imgSrc, true);
	ctx.save();
	ctx.rotateAboutDeg(angle, x, y);
	ctx.drawImage(ruler, x, y, width, width * ratio);
	ctx.restore();
};

// Labels
const hrt2 = Math.sqrt(2) / 2;
const D = {
	N: [0, -0.7],
	NE: [hrt2 * 0.7, -hrt2 * 0.7],
	E: [0.7, 0],
	SE: [hrt2 * 0.7, hrt2 * 0.7],
	S: [0, 0.7],
	SW: [-hrt2 * 0.7, hrt2 * 0.7],
	W: [-0.7, 0],
	NW: [-hrt2 * 0.7, -hrt2 * 0.7],
};

const label = function(ctx, batex, x, y, offset, options = {}) {
	const {
		backColor = null,
		backColorWidthPadding = 0,
		backColorHeightPadding = 0,
	} = options;
	const fontSize = parseInt(ctx.font);
	const distance = 1 + fontSize / 4;

	offset = offset.map(o => Math.round(o * 1000) / 1000);

	const measure = CanvasMath.measureBatex(
		ctx,
		getCanvasMathProps(ctx, {batex: batex, skipLinePadding: true})
	).measure;
	const rect = [x, y, measure.width, measure.height];

	// Horizontal positioning.
	rect[0] += offset[0] * distance;
	if (offset[0] === 0) {
		// Center align
		rect[0] -= measure.width / 2;
	} else if (offset[0] < 0) {
		// Right align
		rect[0] -= measure.width;
	}

	// Vertical positioning
	rect[1] += offset[1] * distance;
	if (offset[1] === 0) {
		// Middle align
		rect[1] -= measure.height / 2;
	} else if (offset[1] < 0) {
		// Bottom align
		rect[1] -= measure.height;
	}

	if (backColor) {
		const backRect = Math2D.padRect(
			rect,
			backColorHeightPadding,
			backColorWidthPadding
		);
		ctx.save();
		ctx
			.bp()
			.rect(backRect)
			.fill(backColor);
		ctx.restore();
	}

	CanvasMath.drawInRect(
		ctx,
		getCanvasMathProps(ctx, {
			batex: batex,
			rect: rect,
			skipLinePadding: true,
		})
	);

	return rect;
};

//Small function for testing the size of the diagram
//ctx - the canvas context to draw on
//color (optional) - the color to fill everything with
const fillAll = function(ctx, color) {
	ctx.save();
	ctx.fillStyle = color || "rgba(255,0,255,0.2)";
	const size = ctx.getSize();
	ctx.fillRect(0, 0, size.width, size.height);
	ctx.restore();
};

module.exports = {
	getCanvasMathProps,
	drawBatex,
	measureBatex,
	drawBatexAtCenter,
	drawBatexInRect,
	getArrowDimensions,
	drawArrow,
	drawQuadArrow,
	drawToothpick,
	drawProtractor,
	drawRuler,
	D,
	Dir: D,
	label,
	fillAll,
	console: fakeConsole,
};
